// @ts-strict-ignore
import {apiClient} from 'common/util/api-client';
import {SetupGuideResponse} from 'common/onboarding/immersive-onboarding/types';

import {appApiUrl} from '../utils';

const API_ENDPOINT = `setup-guide`;
export const V1 = `v1/`;
export const V1_1 = `v1.1/`;

export type Version = typeof V1 | typeof V1_1;

const localStorageKey = (projectId: number) => `cached_setup_guide_info_data_${projectId}`;

const CACHE_EXPIRATION = 4 * 60 * 60 * 1000;

export function getImmersiveOnboardingSetupGuideInfo(
  projectId: number,
  version: Version = V1,
  forceRefresh = false,
): Promise<SetupGuideResponse> {
  const cachedData: {time: Date; setupGuideInfo: SetupGuideResponse} = JSON.parse(
    localStorage.getItem(localStorageKey(projectId)),
  );
  const invalidateCache = cachedData && Date.now().valueOf() - new Date(cachedData.time).valueOf() > CACHE_EXPIRATION;

  if (invalidateCache) {
    localStorage.removeItem(localStorageKey(projectId));
  }

  if (
    cachedData &&
    cachedData.setupGuideInfo &&
    !forceRefresh &&
    !invalidateCache &&
    cachedData[`version`] === version
  ) {
    // Promisifying return to match the return type so we can handle it in a unified manner
    return new Promise((resolve) => resolve(cachedData.setupGuideInfo));
  } else {
    return apiClient
      .get(appApiUrl(version + API_ENDPOINT, {projectId}), undefined)
      .then((setupGuideInfo: Promise<SetupGuideResponse>) => {
        localStorage.setItem(
          localStorageKey(projectId),
          JSON.stringify({
            setupGuideInfo,
            time: Date.now(),
            version,
          }),
        );
        return setupGuideInfo;
      });
  }
}

export function updateSetupGuideInfo(
  projectId: number,
  industry: string,
  events?: string[],
  // for v1.1
  eventPropertiesMappings?: {[key: string]: string[]},
): Promise<SetupGuideResponse> {
  return (
    apiClient
      // Patch returns object as a whole - overwrite the cached data
      .patch(appApiUrl((eventPropertiesMappings ? V1_1 : V1) + API_ENDPOINT, {projectId}), {
        industry,
        ...(events ? {events} : {}),
        ...(eventPropertiesMappings ? {events_properties_map: eventPropertiesMappings} : {}),
      })
      .then((setupGuideInfo: Promise<SetupGuideResponse>) => {
        localStorage.setItem(
          localStorageKey(projectId),
          JSON.stringify({
            setupGuideInfo,
            time: Date.now(),
            version: eventPropertiesMappings ? V1_1 : V1,
          }),
        );
        return setupGuideInfo;
      })
      .catch((err) => {
        return err;
      })
  );
}

export function updateStepsCompleted(projectId: number, stepsCompleted: string[]): Promise<SetupGuideResponse> {
  return apiClient
    .patch(appApiUrl(`${API_ENDPOINT}/completed-step`, {projectId}), {
      steps_completed: stepsCompleted,
    })
    .then(() => {
      const cachedData: {time: Date; setupGuideInfo: SetupGuideResponse} = JSON.parse(
        localStorage.getItem(localStorageKey(projectId)),
      );
      cachedData.setupGuideInfo.steps_completed = stepsCompleted;
      localStorage.setItem(
        localStorageKey(projectId),
        JSON.stringify({
          ...cachedData,
          time: Date.now(),
        }),
      );
      return cachedData.setupGuideInfo;
    })
    .catch((err) => {
      return err;
    });
}
